import React, { useMemo } from 'react';
import { motion } from 'framer-motion';
import { 
  BarChart, 
  Bar, 
  XAxis, 
  YAxis, 
  CartesianGrid, 
  Tooltip, 
  ResponsiveContainer,
  PieChart,
  Pie,
  Cell
} from 'recharts';
import { 
  BookOpen, 
  CheckCircle, 
  Clock, 
  Target, 
  DollarSign,
  Star,
  PieChart as PieChartIcon,
  BookText,
  BookX,
  User,
  MapPin,
  Languages
} from 'lucide-react';
import { useAppState } from '../context/AppStateContext';

const NovelStatistics: React.FC = () => {
  const { state } = useAppState();

  const statistics = useMemo(() => {
    const libros = state.libros.filter(book => book.estado !== 'wishlist');
    
    // Estadísticas básicas de novelas
    const totalLibros = libros.length;
    const librosLeidos = libros.filter(book => book.estado === 'leido').length;
    const librosLeyendo = libros.filter(book => book.estado === 'leyendo').length;
    const librosTBR = libros.filter(book => book.estado === 'tbr').length;
    const librosAbandonados = libros.filter(book => book.estado === 'abandonado').length;
    const librosPrestados = libros.filter(book => book.prestado).length;
    
    // Progreso de lectura
    const progresoLectura = totalLibros > 0 ? (librosLeidos / totalLibros) * 100 : 0;
    
    // Valor total
    const valorTotal = libros
      .filter(book => book.precio && book.precio > 0)
      .reduce((sum, book) => sum + (book.precio || 0), 0);
    
    // Páginas totales
    const paginasTotales = libros
      .filter(book => book.estado === 'leido')
      .reduce((sum, book) => sum + (book.paginas || 0), 0);
    
    // Calificación promedio
    const librosConCalificacion = libros.filter(book => book.calificacion && book.calificacion > 0);
    const calificacionPromedio = librosConCalificacion.length > 0 
      ? librosConCalificacion.reduce((sum, book) => sum + (book.calificacion || 0), 0) / librosConCalificacion.length
      : 0;
    
    // Estadísticas por género
    const generos: Record<string, { total: number; leidos: number; valor: number }> = {};
    libros.forEach(book => {
      if (book.genero) {
        if (!generos[book.genero]) {
          generos[book.genero] = { total: 0, leidos: 0, valor: 0 };
        }
        generos[book.genero].total++;
        if (book.estado === 'leido') generos[book.genero].leidos++;
        if (book.precio) generos[book.genero].valor += book.precio;
      }
    });
    
    // Estadísticas por editorial
    const editoriales: Record<string, { total: number; leidos: number; valor: number }> = {};
    libros.forEach(book => {
      if (book.editorial) {
        if (!editoriales[book.editorial]) {
          editoriales[book.editorial] = { total: 0, leidos: 0, valor: 0 };
        }
        editoriales[book.editorial].total++;
        if (book.estado === 'leido') editoriales[book.editorial].leidos++;
        if (book.precio) editoriales[book.editorial].valor += book.precio;
      }
    });
    
    // Estadísticas por idioma
    const idiomas: Record<string, { total: number; leidos: number; valor: number }> = {};
    libros.forEach(book => {
      if (book.idioma) {
        if (!idiomas[book.idioma]) {
          idiomas[book.idioma] = { total: 0, leidos: 0, valor: 0 };
        }
        idiomas[book.idioma].total++;
        if (book.estado === 'leido') idiomas[book.idioma].leidos++;
        if (book.precio) idiomas[book.idioma].valor += book.precio;
      }
    });
    
    // Estadísticas por formato
    const formatos: Record<string, { total: number; leidos: number; valor: number }> = {};
    libros.forEach(book => {
      const formato = book.formato || 'fisico';
      if (!formatos[formato]) {
        formatos[formato] = { total: 0, leidos: 0, valor: 0 };
      }
      formatos[formato].total++;
      if (book.estado === 'leido') formatos[formato].leidos++;
      if (book.precio) formatos[formato].valor += book.precio;
    });
    
    // Autores más leídos
    const autores: Record<string, { total: number; leidos: number; valor: number }> = {};
    libros.forEach(book => {
      if (book.autor) {
        if (!autores[book.autor]) {
          autores[book.autor] = { total: 0, leidos: 0, valor: 0 };
        }
        autores[book.autor].total++;
        if (book.estado === 'leido') autores[book.autor].leidos++;
        if (book.precio) autores[book.autor].valor += book.precio;
      }
    });
    
    // Top autores por libros leídos
    const topAutores = Object.entries(autores)
      .map(([autor, stats]) => ({
        autor,
        total: stats.total,
        leidos: stats.leidos,
        valor: stats.valor
      }))
      .sort((a, b) => b.leidos - a.leidos)
      .slice(0, 10);
    
    // Top géneros por total
    const topGeneros = Object.entries(generos)
      .map(([genero, stats]) => ({
        genero,
        total: stats.total,
        leidos: stats.leidos,
        valor: stats.valor,
        porcentaje: (stats.leidos / stats.total) * 100
      }))
      .sort((a, b) => b.total - a.total)
      .slice(0, 8);
    
    // Top editoriales por total
    const topEditoriales = Object.entries(editoriales)
      .map(([editorial, stats]) => ({
        editorial,
        total: stats.total,
        leidos: stats.leidos,
        valor: stats.valor
      }))
      .sort((a, b) => b.total - a.total)
      .slice(0, 8);
    
    // Top idiomas por total
    const topIdiomas = Object.entries(idiomas)
      .map(([idioma, stats]) => ({
        idioma,
        total: stats.total,
        leidos: stats.leidos,
        valor: stats.valor
      }))
      .sort((a, b) => b.total - a.total)
      .slice(0, 6);
    
    // Top formatos por total
    const topFormatos = Object.entries(formatos)
      .map(([formato, stats]) => ({
        formato,
        total: stats.total,
        leidos: stats.leidos,
        valor: stats.valor
      }))
      .sort((a, b) => b.total - a.total);
    
    // Distribución de calificaciones
    const calificaciones: Record<number, number> = {};
    librosConCalificacion.forEach(book => {
      const cal = Math.round(book.calificacion!);
      calificaciones[cal] = (calificaciones[cal] || 0) + 1;
    });
    
    const distribucionCalificaciones = Object.entries(calificaciones)
      .map(([cal, count]) => ({
        calificacion: parseInt(cal),
        cantidad: count
      }))
      .sort((a, b) => a.calificacion - b.calificacion);
    
    return {
      totalLibros,
      librosLeidos,
      librosLeyendo,
      librosTBR,
      librosAbandonados,
      librosPrestados,
      progresoLectura,
      valorTotal,
      paginasTotales,
      calificacionPromedio,
      topAutores,
      topGeneros,
      topEditoriales,
      topIdiomas,
      topFormatos,
      distribucionCalificaciones
    };
  }, [state.libros]);

  const colors = ['#3B82F6', '#10B981', '#F59E0B', '#EF4444', '#8B5CF6', '#06B6D4', '#F97316', '#EC4899'];

  return (
    <div className="space-y-8">
      {/* Tarjetas de resumen */}
      <div className="grid grid-cols-2 md:grid-cols-4 lg:grid-cols-6 gap-4">
        {[
          { title: 'Total Novelas', value: statistics.totalLibros, icon: BookOpen, color: 'bg-blue-500' },
          { title: 'Leídas', value: statistics.librosLeidos, icon: CheckCircle, color: 'bg-green-500' },
          { title: 'Leyendo', value: statistics.librosLeyendo, icon: Clock, color: 'bg-yellow-500' },
          { title: 'TBR', value: statistics.librosTBR, icon: BookText, color: 'bg-orange-500' },
          { title: 'Abandonadas', value: statistics.librosAbandonados, icon: BookX, color: 'bg-red-500' },
          { title: 'Valor Total', value: `${statistics.valorTotal.toLocaleString('es-ES', { style: 'currency', currency: 'EUR' })}`, icon: DollarSign, color: 'bg-emerald-500' }
        ].map((stat, index) => (
          <motion.div
            key={stat.title}
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: index * 0.1 }}
            className="glass-effect rounded-xl p-4 text-center"
          >
            <div className={`inline-flex items-center justify-center w-12 h-12 rounded-lg ${stat.color} mb-3`}>
              <stat.icon className="h-6 w-6 text-white" />
            </div>
            <div className="text-2xl font-bold text-slate-900 dark:text-white mb-1">
              {stat.value}
            </div>
            <div className="text-sm font-medium text-slate-700 dark:text-slate-300">
              {stat.title}
            </div>
          </motion.div>
        ))}
      </div>

      {/* Progreso de lectura */}
      <motion.div
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ delay: 0.3 }}
        className="glass-effect rounded-xl p-6"
      >
        <h3 className="text-lg font-semibold text-slate-900 dark:text-white mb-4 flex items-center">
          <Target className="h-5 w-5 mr-2 text-blue-500" />
          Progreso de Lectura
        </h3>
        <div className="space-y-4">
          <div className="flex items-center justify-between">
            <span className="text-sm text-slate-600 dark:text-slate-400">Progreso General</span>
            <span className="text-sm font-medium text-slate-900 dark:text-white">
              {statistics.progresoLectura.toFixed(1)}%
            </span>
          </div>
          <div className="w-full bg-slate-200 dark:bg-slate-700 rounded-full h-3">
            <div 
              className="bg-gradient-to-r from-blue-500 to-blue-600 h-3 rounded-full transition-all duration-500"
              style={{ width: `${Math.min(100, statistics.progresoLectura)}%` }}
            />
          </div>
          <div className="text-center text-sm text-slate-600 dark:text-slate-400">
            {statistics.librosLeidos} de {statistics.totalLibros} novelas completadas
          </div>
        </div>
      </motion.div>

      {/* Distribución por género */}
      <motion.div
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ delay: 0.4 }}
        className="glass-effect rounded-xl p-6"
      >
        <h3 className="text-lg font-semibold text-slate-900 dark:text-white mb-6 flex items-center">
          <PieChartIcon className="h-5 w-5 mr-2 text-purple-500" />
          Distribución por Género
        </h3>
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
          <ResponsiveContainer width="100%" height={300}>
            <PieChart>
              <Pie
                data={statistics.topGeneros}
                cx="50%"
                cy="50%"
                outerRadius={80}
                fill="#8884d8"
                dataKey="total"
                label={({ genero, total }) => `${genero} (${total})`}
              >
                {statistics.topGeneros.map((entry, index) => (
                  <Cell key={`cell-${index}`} fill={colors[index % colors.length]} />
                ))}
              </Pie>
              <Tooltip />
            </PieChart>
          </ResponsiveContainer>
          
          <div className="space-y-3">
            {statistics.topGeneros.map((genero, index) => (
              <div key={genero.genero} className="flex items-center justify-between p-3 bg-slate-50 dark:bg-slate-800 rounded-lg">
                <div className="flex items-center space-x-3">
                  <div className="w-4 h-4 rounded-full" style={{ backgroundColor: colors[index % colors.length] }} />
                  <span className="font-medium text-slate-900 dark:text-white capitalize">
                    {genero.genero}
                  </span>
                </div>
                <div className="flex items-center space-x-2">
                  <span className="text-sm text-slate-600 dark:text-slate-400">
                    {genero.leidos}/{genero.total}
                  </span>
                  <div className="w-16 bg-slate-200 dark:bg-slate-700 rounded-full h-2">
                    <div 
                      className="bg-purple-500 h-2 rounded-full"
                      style={{ width: `${genero.porcentaje}%` }}
                    />
                  </div>
                </div>
              </div>
            ))}
          </div>
        </div>
      </motion.div>

      {/* Top autores */}
      <motion.div
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ delay: 0.5 }}
        className="glass-effect rounded-xl p-6"
      >
        <h3 className="text-lg font-semibold text-slate-900 dark:text-white mb-6 flex items-center">
          <User className="h-5 w-5 mr-2 text-green-500" />
          Top Autores por Novelas Leídas
        </h3>
        <div className="space-y-3">
          {statistics.topAutores.map((autor, index) => (
            <div key={autor.autor} className="flex items-center justify-between p-3 bg-slate-50 dark:bg-slate-800 rounded-lg">
              <div className="flex items-center space-x-3">
                <div className="w-6 h-6 bg-green-500 text-white rounded-full flex items-center justify-center text-xs font-bold">
                  {index + 1}
                </div>
                <span className="font-medium text-slate-900 dark:text-white">
                  {autor.autor}
                </span>
              </div>
              <div className="flex items-center space-x-4">
                <span className="text-sm text-slate-600 dark:text-slate-400">
                  {autor.leidos} leídas
                </span>
                <span className="text-sm text-slate-600 dark:text-slate-400">
                  {autor.total} total
                </span>
                <span className="text-sm text-slate-600 dark:text-slate-400">
                  {autor.valor.toLocaleString('es-ES', { style: 'currency', currency: 'EUR' })}
                </span>
              </div>
            </div>
          ))}
        </div>
      </motion.div>

      {/* Distribución por editorial */}
      <motion.div
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ delay: 0.6 }}
        className="glass-effect rounded-xl p-6"
      >
        <h3 className="text-lg font-semibold text-slate-900 dark:text-white mb-6 flex items-center">
          <MapPin className="h-5 w-5 mr-2 text-indigo-500" />
          Distribución por Editorial
        </h3>
        <ResponsiveContainer width="100%" height={300}>
          <BarChart data={statistics.topEditoriales}>
            <CartesianGrid strokeDasharray="3 3" />
            <XAxis dataKey="editorial" />
            <YAxis />
            <Tooltip />
            <Bar dataKey="total" fill="#6366F1" />
          </BarChart>
        </ResponsiveContainer>
      </motion.div>

      {/* Distribución por idioma */}
      <motion.div
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ delay: 0.7 }}
        className="glass-effect rounded-xl p-6"
      >
        <h3 className="text-lg font-semibold text-slate-900 dark:text-white mb-6 flex items-center">
          <Languages className="h-5 w-5 mr-2 text-teal-500" />
          Distribución por Idioma
        </h3>
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
          {statistics.topIdiomas.map((idioma, index) => (
            <div key={idioma.idioma} className="p-4 bg-slate-50 dark:bg-slate-800 rounded-lg text-center">
              <div className="text-2xl font-bold text-slate-900 dark:text-white mb-2">
                {idioma.total}
              </div>
              <div className="text-sm text-slate-600 dark:text-slate-400 capitalize">
                {idioma.idioma}
              </div>
              <div className="text-xs text-slate-500 dark:text-slate-500 mt-1">
                {idioma.leidos} leídas
              </div>
            </div>
          ))}
        </div>
      </motion.div>

      {/* Distribución por formato */}
      <motion.div
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ delay: 0.8 }}
        className="glass-effect rounded-xl p-6"
      >
        <h3 className="text-lg font-semibold text-slate-900 dark:text-white mb-6 flex items-center">
          <BookOpen className="h-5 w-5 mr-2 text-orange-500" />
          Distribución por Formato
        </h3>
        <ResponsiveContainer width="100%" height={300}>
          <BarChart data={statistics.topFormatos}>
            <CartesianGrid strokeDasharray="3 3" />
            <XAxis dataKey="formato" />
            <YAxis />
            <Tooltip />
            <Bar dataKey="total" fill="#F97316" />
          </BarChart>
        </ResponsiveContainer>
      </motion.div>

      {/* Distribución de calificaciones */}
      <motion.div
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ delay: 0.9 }}
        className="glass-effect rounded-xl p-6"
      >
        <h3 className="text-lg font-semibold text-slate-900 dark:text-white mb-6 flex items-center">
          <Star className="h-5 w-5 mr-2 text-yellow-500" />
          Distribución de Calificaciones
        </h3>
        <ResponsiveContainer width="100%" height={300}>
          <BarChart data={statistics.distribucionCalificaciones}>
            <CartesianGrid strokeDasharray="3 3" />
            <XAxis dataKey="calificacion" />
            <YAxis />
            <Tooltip />
            <Bar dataKey="cantidad" fill="#F59E0B" />
          </BarChart>
        </ResponsiveContainer>
        <div className="text-center mt-4 text-sm text-slate-600 dark:text-slate-400">
          Calificación promedio: {statistics.calificacionPromedio.toFixed(1)}/5
        </div>
      </motion.div>
    </div>
  );
};

export default NovelStatistics;